/**
 * Instagram feed display aspect-ratio helpers.
 *
 * Instagram clamps a feed media's aspect ratio (width / height) into a fixed
 * range: portrait no taller than 3:4 and landscape no wider than 1.91:1.
 * Anything outside that range is centre-cropped to the nearest bound.
 * Use this to make a preview match what Instagram will actually display.
 *
 * Note: since the 2025 feed update Instagram displays portrait posts down to
 * 3:4 (1080x1440, w/h = 0.75), taller than the older 4:5 (0.8) limit.
 */

/** Minimum width/height Instagram allows in feed (3:4 portrait = 0.75). */
export const IG_MIN_ASPECT = 3 / 4;

/** Maximum width/height Instagram allows in feed (1.91:1 landscape). */
export const IG_MAX_ASPECT = 1.91;

/**
 * Clamps a natural width/height ratio into Instagram's feed range.
 * Invalid input (0, NaN, negative) falls back to the 4:5 portrait default.
 */
export function clampInstagramAspect(naturalAspect: number): number {
  if (!Number.isFinite(naturalAspect) || naturalAspect <= 0) {
    return naturalAspect === Infinity ? IG_MAX_ASPECT : IG_MIN_ASPECT;
  }
  return Math.min(IG_MAX_ASPECT, Math.max(IG_MIN_ASPECT, naturalAspect));
}
